import { QueryClient } from "@tanstack/react-query";
import type { ListListingsQuery } from "@hybrid-escrow/shared";
import { ApiClientError, type DisputesQuery, type ListingsQuery } from "./api-client";

/**
 * Don't burn retries against most 4xx client errors (validation, not-found, forbidden, etc) —
 * retrying a genuinely-wrong request just delays showing the real error.
 *
 * 401 is the deliberate exception: `accessTokenBridge.ts`'s `getBearerToken()` already retries
 * once internally, but this is a second, independent line of defense at the query layer for the
 * same class of issue (a request going out with no/stale token during a brief CDP SDK settling
 * window right after a wallet action confirms — see its docblock for the full story). A real
 * signed-out session still fails the same way after these retries exhaust; this only changes how
 * fast a *transient* 401 recovers.
 */
function shouldRetry(failureCount: number, error: unknown): boolean {
  if (error instanceof ApiClientError && error.status === 401) {
    return failureCount < 2;
  }
  if (error instanceof ApiClientError && error.status >= 400 && error.status < 500) {
    return false;
  }
  return failureCount < 2;
}

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: shouldRetry,
      refetchOnWindowFocus: false,
      staleTime: 15_000,
    },
    mutations: {
      retry: false,
    },
  },
});

export const queryKeys = {
  publicConfig: ["public-config"] as const,
  me: ["me"] as const,
  listings: (query: Partial<ListListingsQuery> = {}) => ["listings", query] as const,
  listing: (id: string) => ["listing", id] as const,
  sellerListings: ["seller-listings"] as const,
  watchlist: ["watchlist"] as const,
  savedSearches: ["saved-searches"] as const,
  offer: (id: string) => ["offer", id] as const,
  licenseKeys: (listingId: string) => ["license-keys", listingId] as const,
  notifications: ["notifications"] as const,
  notificationsUnreadCount: ["notifications-unread-count"] as const,
  sellerProfile: (sellerId: string) => ["seller-profile", sellerId] as const,
  myOffers: ["my-offers"] as const,
  sellerOffers: ["seller-offers"] as const,
  orders: (query: ListingsQuery = {}) => ["orders", query] as const,
  order: (orderId: string) => ["order", orderId] as const,
  dispute: (disputeId: string) => ["dispute", disputeId] as const,
  disputes: (query: DisputesQuery = {}) => ["disputes", query] as const,
  sellerRating: (sellerId: string) => ["seller-rating", sellerId] as const,
  sellerReviews: (sellerId: string) => ["seller-reviews", sellerId] as const,
  conversations: ["conversations"] as const,
  messages: (conversationId: string) => ["messages", conversationId] as const,
  adminMetrics: ["admin-metrics"] as const,
  adminUsers: (search: string) => ["admin-users", search] as const,
  adminUserDetail: (userId: string) => ["admin-user-detail", userId] as const,
  adminListings: ["admin-listings"] as const,
  adminDisputes: ["admin-disputes"] as const,
  adminFlaggedMessages: ["admin-flagged-messages"] as const,
  adminAuditEvents: (action: string) => ["admin-audit-events", action] as const,
  adminMaintenanceMode: ["admin-maintenance-mode"] as const,
  profileDetails: ["profile-details"] as const,
  notificationPreferences: ["notification-preferences"] as const,
  sellerAnalytics: ["seller-analytics"] as const,
  tickets: ["tickets"] as const,
  ticket: (ticketId: string) => ["ticket", ticketId] as const,
  adminTickets: (status: string) => ["admin-tickets", status] as const,
  adminTicket: (ticketId: string) => ["admin-ticket", ticketId] as const,
  adminCoupons: ["admin-coupons"] as const,
  adminAccountTransfers: ["admin-account-transfers"] as const,
  adminDomainTransfers: ["admin-domain-transfers"] as const,
  adminPosts: ["admin-posts"] as const,
  adminPost: (postId: string) => ["admin-post", postId] as const,
  posts: ["posts"] as const,
  post: (slug: string) => ["post", slug] as const,
};
